import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import * as React from 'react';
import RSelect, { components, createFilter } from 'react-select';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { ChevronDown, Checkmark, Close } from '@az/starc-ui-icons';
import { Label } from "../_private/label";
import { Actionable } from "../actionable";
import { useFormControl } from "../form-control";
import { Icon } from "../icon";
import { Text } from "../text";
import { View } from "../view";
import { useElementId } from "../../hooks/useElementId";
import { classNames, narrowString } from "../../utils/helpers";
import { INTERNAL_LABEL_VARIANTS, EXTERNAL_LABEL_VARIANTS } from './Select.types';
import s from './Select.module.css';
export const Select = React.forwardRef((props, ref) => {
    const { closeOnSelect: closeOnSelectOption, attributes, className, defaultValue = null, disabled: disabledProp, hasError: hasErrorProp, required, filterConfig, inputAttributes, clearable = true, hasCheck, value, icon, id, combobox, multiSelect, label, highlightQuery, maxOptionsShown, name, backspaceRemovesValue = true, onValueChange, options, size = 'large', variant = 'floating', open, defaultOpen, menuPosition = 'auto', menuZIndex = 2, onOpen, onClose, hasShiftedLabel, customOptions, ariaLiveMessages, placeholder = '', responsiveWidth = false, minWidth = '0px', maxWidth = 'none', } = props;
    const { hasError: ctxError, disabled: ctxDisabled } = useFormControl();
    const disabled = disabledProp !== null && disabledProp !== void 0 ? disabledProp : ctxDisabled;
    const hasError = hasErrorProp !== null && hasErrorProp !== void 0 ? hasErrorProp : ctxError;
    const selectId = useElementId(id);
    const innerRef = React.useRef(null);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    React.useImperativeHandle(ref, () => innerRef.current, []);
    const internalLabelVariant = narrowString(variant, INTERNAL_LABEL_VARIANTS);
    const externalLabelVariant = narrowString(variant, EXTERNAL_LABEL_VARIANTS);
    const rootClassName = classNames(s.root, s[`--size-${size}`], disabled && s['--disabled'], hasError && s[`error`], className);
    const labelClassName = classNames(s.label, s[`--size-${size}`], internalLabelVariant && s[`--variant-${internalLabelVariant}`]);
    const staticLabelClassName = classNames(s['static-label'], externalLabelVariant && s[`--variant-${externalLabelVariant}`]);
    const chevronClassName = classNames(s.chevron);
    const chevronIconClassName = classNames(s['chevron-icon']);
    const classes = {
        container: () => classNames(variant === 'left-label' && s['container']),
        option: ({ isDisabled, isFocused }) => classNames(s['options-item'], isDisabled && s['--disabled'], isFocused && s['--active']),
        control: ({ hasValue, isFocused, menuIsOpen }) => classNames(rootClassName, hasValue && s['--selected'], isFocused && menuIsOpen && s['--focused']),
        indicatorSeparator: () => s.separator,
        dropdownIndicator: ({ selectProps: { menuIsOpen } }) => classNames(s['dropdown-indicator'], menuIsOpen && s['--menu-open']),
        menu: () => s.options,
        multiValue: () => s['multi-value-item'],
        multiValueLabel: () => s['multi-value-label'],
        input: () => s.combobox,
    };
    const styles = {
        dropdownIndicator: (base, dropdownIndicatorProps) => (Object.assign(Object.assign({}, base), { transition: 'all .2s ease', transform: dropdownIndicatorProps.selectProps.menuIsOpen ? 'rotate(180deg)' : '' })),
        clearIndicator: base => (Object.assign(Object.assign({}, base), { display: combobox || multiSelect ? 'block' : 'none', marginRight: combobox || multiSelect ? 'var(--st-unit-2)' : '0' })),
        menu: base => (Object.assign(Object.assign({}, base), { zIndex: menuZIndex, marginTop: 'var(--st-unit-1)' })),
        option: base => (Object.assign(Object.assign({}, base), { display: 'flex', minHeight: 'calc(var(--st-unit-11) + 2px)', border: 0 })),
        valueContainer: (base, valueContainerProps) => (Object.assign(Object.assign({}, base), { display: 'flex', height: '100%', overflow: 'visible', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: `calc(100% - ${(!!combobox || !!multiSelect) && valueContainerProps.hasValue
                ? // Width of controls with clear indicator
                    '69px'
                : // Width of controls without clear indicator
                    '41px'})` })),
        input: (base, inputProps) => (Object.assign(Object.assign({}, base), { marginTop: size === 'large' &&
                variant === 'alt' &&
                (inputProps.hasValue || !!inputProps.selectProps.inputValue)
                ? 'var(--st-select-input-margin)'
                : base.marginTop, overflow: 'hidden' })),
        singleValue: (base, singleValueProps) => (Object.assign(Object.assign({}, base), { float: 'right', width: 'auto', overflow: responsiveWidth ? 'visible' : 'hidden', marginTop: size === 'large' &&
                variant === 'alt' &&
                (singleValueProps.hasValue || !!singleValueProps.selectProps.inputValue)
                ? 'var(--st-select-input-margin)'
                : base.marginTop })),
        control: base => (Object.assign(Object.assign({}, base), { width: responsiveWidth ? 'fit-content' : '100%', minWidth: minWidth, maxWidth: maxWidth, outline: undefined, flexWrap: undefined })),
    };
    const DropdownIndicator = (dropdownIndicatorProps) => (_jsx(components.DropdownIndicator, Object.assign({}, dropdownIndicatorProps, { children: _jsx(View, { className: chevronClassName, children: _jsx(Icon, { svg: ChevronDown, className: chevronIconClassName }) }) })));
    const ValueContainer = (valueContainerProps) => (_jsx(components.ValueContainer, Object.assign({}, valueContainerProps, { children: _jsxs(View, { direction: "row", align: "center", height: "100%", width: "100%", children: [(props.startElement || icon) && (_jsx(View, { className: s['start-element'], children: props.startElement || (icon && _jsx(Icon, { svg: icon, size: 5 })) })), _jsxs(View.Item, { grow: true, className: s['inner-value-container'], children: [internalLabelVariant && label ? (_jsx(Label, { text: label, className: classNames(labelClassName, valueContainerProps.hasValue ||
                                !!valueContainerProps.selectProps.inputValue ||
                                hasShiftedLabel
                                ? s['--shifted']
                                : ''), disabled: disabled, required: required, htmlFor: `${selectId}-input`, shifted: valueContainerProps.hasValue ||
                                !!valueContainerProps.selectProps.inputValue ||
                                hasShiftedLabel, variant: internalLabelVariant })) : null, multiSelect ? (_jsx(View, { direction: "row", wrap: false, overflow: "hidden", children: valueContainerProps.children })) : (valueContainerProps.children)] }), props.endElement && _jsx(View, { className: s['end-element'], children: props.endElement })] }) })));
    const Input = (inputProps) => {
        return _jsx(components.Input, Object.assign({}, inputProps, { "data-testid": "st-select-input" }));
    };
    const Option = (optionProps) => {
        const renderFont = () => {
            const font = {
                size: '087',
                weight: 'regular',
                color: undefined,
            };
            if (optionProps.isDisabled) {
                font.color = 'gray-400';
            }
            if (optionProps.isSelected) {
                font.weight = 'bold';
            }
            return font;
        };
        const renderOptionIcon = () => {
            if (hasCheck && optionProps.isSelected && !optionProps.isDisabled) {
                return (_jsx(View, { className: s['options-item--icon'], attributes: { 'aria-hidden': true }, justify: "center", children: _jsx(Icon, { svg: Checkmark }) }));
            }
            else {
                return null;
            }
        };
        const renderOptionText = (suggestionText) => {
            const matches = match(suggestionText, optionProps.selectProps.inputValue, {
                findAllOccurrences: true,
                insideWords: true,
            });
            const parts = parse(suggestionText, matches);
            return (_jsx(View, { direction: "row", children: parts.map((part, index) => {
                    return (_jsx(Text, { as: "span", weight: part.highlight ? 'bold' : renderFont().weight, color: renderFont().color, attributes: {
                            style: {
                                display: 'flex',
                                whiteSpace: 'pre',
                                lineHeight: 'var(--st-unit-4)',
                            },
                        }, children: part.text }, index));
                }) }));
        };
        return (_jsx(components.Option, Object.assign({}, optionProps, { 
            // @ts-expect-error 'data-testid' does not exist on type 'OptionProps<unknown>' as it is a custom prop.
            innerProps: Object.assign({ 'data-testid': 'st-select-option' }, optionProps.innerProps), children: !customOptions ? (_jsxs(View, { direction: "row", height: "100%", align: "center", children: [renderOptionIcon(), _jsx(View.Item, { children: _jsx(Text, { size: renderFont().size, weight: renderFont().weight, color: renderFont().color, children: highlightQuery ? renderOptionText(optionProps.label) : optionProps.label }) })] })) : (customOptions(optionProps)) })));
    };
    const Control = (props) => {
        return (_jsx(components.Control, Object.assign({}, props, attributes, { children: _jsx(_Fragment, { children: props.children }) })));
    };
    const ClearIndicator = (props) => {
        const handleKeyDown = (e) => {
            var _a;
            if (e.key === 'Enter') {
                props.clearValue();
                (_a = innerRef === null || innerRef === void 0 ? void 0 : innerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        };
        const handleClear = () => {
            var _a;
            props.clearValue();
            (_a = innerRef === null || innerRef === void 0 ? void 0 : innerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        };
        const renderClearButton = () => {
            const clearable = props.selectProps.isMulti || combobox;
            const componentTitle = props.selectProps['aria-label'];
            if (props.selectProps.isClearable && props.hasValue && clearable) {
                return (_jsx(Actionable, { onClick: handleClear, attributes: { onKeyDown: handleKeyDown, 'aria-label': `${componentTitle} clear` }, children: _jsx(Icon, { svg: Close, size: 5 }) }));
            }
            else {
                return null;
            }
        };
        return _jsx(components.ClearIndicator, Object.assign({}, props, { children: renderClearButton() }));
    };
    const customFilter = createFilter(filterConfig || {
        matchFrom: 'start',
    });
    return (_jsxs(View, { align: variant === 'left-label' ? 'center' : undefined, direction: variant === 'left-label' ? 'row' : variant === 'top-label' ? 'column' : undefined, wrap: variant === 'left-label' ? false : undefined, attributes: {
            'data-testid': 'st-select',
            style: {
                position: 'relative',
            },
        }, children: [externalLabelVariant && (_jsx(Label, { text: label, required: required, htmlFor: `${selectId}-input`, variant: "static", className: staticLabelClassName })), _jsx(RSelect, Object.assign({}, inputAttributes, { unstyled: true, 
                //TODO: Update typescript.
                // @ts-expect-error TS can't correctly infer type due to generics
                onChange: onValueChange, placeholder: placeholder, "aria-label": name, id: selectId, inputId: `${selectId}-input`, isMulti: multiSelect, value: value, defaultValue: defaultValue, options: options, isOptionDisabled: (option) => !!(option === null || option === void 0 ? void 0 : option.disabled), name: name, menuIsOpen: open, menuPlacement: menuPosition, defaultMenuIsOpen: defaultOpen, backspaceRemovesValue: backspaceRemovesValue, closeMenuOnSelect: closeOnSelectOption || !multiSelect, hideSelectedOptions: false, tabSelectsValue: false, onMenuClose: onClose, onMenuOpen: onOpen, isClearable: !!clearable || !!multiSelect, isDisabled: disabled, isSearchable: !!combobox, required: required, maxMenuHeight: maxOptionsShown && maxOptionsShown > 0 ? maxOptionsShown * 40 : undefined, 
                // @ts-expect-error TS can't correctly infer type due to generics
                ref: innerRef, components: {
                    DropdownIndicator: DropdownIndicator,
                    Option: Option,
                    Control: Control,
                    ValueContainer: ValueContainer,
                    ClearIndicator: ClearIndicator,
                    Input: Input,
                }, classNames: classes, styles: styles, filterOption: customFilter, ariaLiveMessages: ariaLiveMessages }))] }));
});
Select.displayName = 'Select';
